import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Divider,
  Card,
  CardContent,
  FormHelperText,
  CircularProgress,
  Alert,
  styled,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import useFetchObject from '../../api/useFetchObject';
import useFetchObjects from '../../api/useFetchObjects';
import useUpdate from '../../api/useUpdate';
import { useAuth } from '../../context/AuthContext';

// RTL-aware TextField component
const RTLTextField = styled(TextField)(({ theme, isRtl }) => ({
  ...(isRtl && {
    '& .MuiInputBase-input': {
      textAlign: 'right',
      direction: 'rtl',
      padding: '10px 14px',
    },
    '& .MuiInputLabel-root': {
      right: 25,
      left: 'auto',
      transformOrigin: 'right',
      marginRight: '5px',
    },
    '& .MuiOutlinedInput-root': {
      paddingLeft: 14,
      paddingRight: 0,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      textAlign: 'right',
    },
    '& .MuiFormHelperText-root': {
      textAlign: 'right',
    },
  }),
}));

// RTL-aware FormControl
const RTLFormControl = styled(FormControl)(({ theme, isRtl }) => ({
  ...(isRtl && {
    '& .MuiInputLabel-root': {
      right: 25,
      left: 'auto',
      transformOrigin: 'right',
      marginRight: '5px',
    },
    '& .MuiInputBase-input': {
      textAlign: 'right',
      direction: 'rtl',
    },
    '& .MuiSelect-select': {
      paddingRight: '32px',
      textAlign: 'right',
      paddingLeft: '32px',
    },
    '& .MuiSelect-icon': {
      right: 'auto',
      left: 7,
      pointerEvents: 'none',
      color: theme.palette.action.active,
    },
    '& .MuiFormHelperText-root': {
      textAlign: 'right',
    },
  }),
}));

const DirectorateEdit = () => {
  const { t, i18n } = useTranslation(['directorates', 'common', 'validation']);
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const token = user?.token;
  const isRtl = i18n.language === 'dari' || i18n.language === 'ps';

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    deputy_ministry_id: '',
  });

  // Validation state
  const [errors, setErrors] = useState({});
  
  // Fetch directorate data
  const { 
    data: directorateData, 
    isLoading: loadingDirectorate,
    isError: isDirectorateError,
    error: directorateError
  } = useFetchObject('directorates', `directorates/${id}`, id, token);
  
  // Fetch deputy ministries
  const { 
    data: deputyMinistriesData, 
    isLoading: loadingDeputyMinistries 
  } = useFetchObjects('deputy-ministries', {}, token);
  
  const deputyMinistries = Array.isArray(deputyMinistriesData) ? deputyMinistriesData : (deputyMinistriesData?.data || []);

  // Update mutation hook
  const { 
    handleUpdate, 
    loading: isUpdating, 
    success 
  } = useUpdate(
    'directorates',
    token,
    '/directorates',
    t('directorates:update_success'),
    t('directorates:update_error')
  );

  // Set form data when directorate data is loaded
  useEffect(() => {
    if (directorateData) {
      // Check if data is nested (API standard response format)
      const directorate = directorateData.data || directorateData;
      
      setFormData({
        name: directorate.name || '',
        description: directorate.description || '',
        deputy_ministry_id: directorate.deputy_ministry_id || '',
      });
    }
  }, [directorateData]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t('validation:required');
    }
    
    if (!formData.deputy_ministry_id) {
      newErrors.deputy_ministry_id = t('validation:required');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    // Create a copy of the form data
    const submissionData = { ...formData };
    
    // Submit form data
    handleUpdate(id, submissionData);
  };

  if (loadingDirectorate) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isDirectorateError || !directorateData) {
    return (
      <Box>
        <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
          {t('directorates:not_found')}
        </Typography>
        <Alert severity="error" sx={{ mb: 3 }}>
          {directorateError?.message || t('directorates:error_occurred')}
        </Alert>
        <Button
          component={RouterLink}
          to="/directorates"
          startIcon={<ArrowBackIcon />}
        >
          {t('common:back_to_list')}
        </Button>
      </Box>
    );
  }

  return (
    <Box 
      component="form" 
      onSubmit={handleSubmit}
      dir={isRtl ? 'rtl' : 'ltr'}
      sx={{ textAlign: isRtl ? 'right' : 'left' }}
    >
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        mb: 3, 
        flexDirection: isRtl ? 'row-reverse' : 'row' 
      }}>
        <Button
          component={RouterLink}
          to="/directorates"
          startIcon={!isRtl && <ArrowBackIcon />}
          endIcon={isRtl && <ArrowBackIcon />}
          sx={{ mr: isRtl ? 0 : 2, ml: isRtl ? 2 : 0 }}
        >
          {t('common:back')}
        </Button>
        <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
          {t('directorates:edit_title')}
        </Typography>
      </Box>

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('directorates:basic_info')}
          </Typography>
          
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <RTLTextField
                fullWidth
                name="name"
                label={t('directorates:name')}
                value={formData.name}
                onChange={handleChange}
                error={!!errors.name}
                helperText={errors.name}
                required
                isRtl={isRtl}
                InputProps={{
                  dir: isRtl ? 'rtl' : 'ltr',
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <RTLFormControl fullWidth error={!!errors.deputy_ministry_id} required isRtl={isRtl}>
                <InputLabel id="deputy-ministry-label">
                  {t('directorates:deputy_ministry')}
                </InputLabel>
                <Select
                  labelId="deputy-ministry-label"
                  name="deputy_ministry_id"
                  label={t('directorates:deputy_ministry')}
                  value={formData.deputy_ministry_id}
                  onChange={handleChange}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: isRtl ? 'right' : 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: isRtl ? 'right' : 'left',
                    },
                    PaperProps: {
                      style: {
                        direction: isRtl ? 'rtl' : 'ltr',
                      },
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>{t('directorates:select')}</em>
                  </MenuItem>
                  {loadingDeputyMinistries ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} /> {t('common:loading')}
                    </MenuItem>
                  ) : (
                    deputyMinistries.map((ministry) => (
                      <MenuItem key={ministry.id} value={ministry.id}>
                        {ministry.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
                {errors.deputy_ministry_id && (
                  <FormHelperText>{errors.deputy_ministry_id}</FormHelperText>
                )}
              </RTLFormControl>
            </Grid>
            
            <Grid item xs={12}>
              <RTLTextField
                fullWidth
                name="description"
                label={t('directorates:description')}
                value={formData.description}
                onChange={handleChange}
                multiline
                rows={3}
                isRtl={isRtl}
                InputProps={{
                  dir: isRtl ? 'rtl' : 'ltr',
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Divider sx={{ my: 4 }} />

      <Box sx={{ display: 'flex', justifyContent: isRtl ? 'flex-start' : 'flex-end', gap: 2 }}>
        <Button
          variant="outlined"
          component={RouterLink}
          to="/directorates"
        >
          {t('common:cancel')}
        </Button>
        <Button
          variant="contained"
          type="submit"
          disabled={isUpdating}
        >
          {isUpdating ? (
            <CircularProgress size={24} />
          ) : (
            t('common:save')
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default DirectorateEdit; 